import React from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from 'utils';
import { motion } from 'framer-motion';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Shield, Clock, Wrench, ArrowRight } from 'lucide-react';

export default function HeroSection() {
    return (
        <section className="relative min-h-screen flex items-center justify-center pt-16 overflow-x-hidden">
            {/* Background Image & Overlay */}
            <div className="absolute inset-0 z-0">
                <img
                    src="/images/hero-background.jpg"
                    alt="Server Room"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/70 to-white/90" />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-20">
                <motion.div
                    className="text-center max-w-5xl mx-auto px-4"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    {/* Badge */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="mb-4 sm:mb-8"
                    >
                        <Badge className="bg-blue-50 text-blue-600 border-blue-200 px-4 py-2 text-sm backdrop-blur-sm">
                            <Shield className="w-4 h-4 mr-2" />
                            Oltre 20 anni di esperienza in Sicilia
                        </Badge>
                    </motion.div>

                    {/* Main Heading */}
                    <motion.h1
                        className="text-3xl sm:text-6xl lg:text-7xl font-bold mb-4 sm:mb-6 leading-tight"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <span className="text-gray-900">Continuità energetica</span>
                        <br />
                        <span className="text-blue-600">per chi non può fermarsi.</span>
                    </motion.h1>

                    {/* Subtitle */}
                    <motion.p
                        className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-10 max-w-3xl mx-auto leading-relaxed"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                    >
                        Vendita, assistenza e riparazione UPS professionali per ospedali,
                        cliniche, aeroporti e aziende. Interventi rapidi in tutta la Sicilia.
                    </motion.p>

                    {/* CTA Buttons */}
                    <motion.div
                        className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-10 sm:mb-16"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                    >
                        <a href="tel:+393296139684">
                            <Button size="lg" className="w-full sm:w-auto rounded-full bg-blue-600 hover:bg-blue-700 text-white px-8 h-12 sm:h-14 text-base gap-2">
                                Assistenza Urgente
                                <ArrowRight className="w-5 h-5" />
                            </Button>
                        </a>
                        <Link to={createPageUrl('Riparazioni')}>
                            <Button size="lg" variant="outline" className="w-full sm:w-auto rounded-full border-gray-300 text-gray-950 bg-white/60 hover:bg-white/80 px-8 h-12 sm:h-14 text-base backdrop-blur-sm">
                                Richiedi Riparazione
                            </Button>
                        </Link>
                        <Link to={createPageUrl('Contatti')}>
                            <Button size="lg" variant="outline" className="w-full sm:w-auto rounded-full border-gray-300 text-gray-950 bg-white/60 hover:bg-white/80 px-8 h-12 sm:h-14 text-base backdrop-blur-sm">
                                Preventivo Gratuito
                            </Button>
                        </Link>
                    </motion.div>

                    {/* Stats */}
                    <motion.div
                        className="grid grid-cols-3 gap-2 sm:gap-8 max-w-4xl mx-auto"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                    >
                        {[
                            { icon: Clock, number: '20+', label: 'Anni di esperienza' },
                            { icon: Shield, number: '500+', label: 'Clienti attivi' },
                            { icon: Wrench, number: '24h', label: 'Tempo medio risposta' }
                        ].map((stat, index) => (
                            <div key={index} className="text-center">
                                <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-4 rounded-full bg-blue-100 flex items-center justify-center backdrop-blur-sm">
                                    <stat.icon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                                </div>
                                <div className="text-2xl sm:text-4xl font-bold text-gray-900 mb-1 sm:mb-2">{stat.number}</div>
                                <div className="text-[10px] sm:text-base text-gray-600 leading-tight">{stat.label}</div>
                            </div>
                        ))}
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}